import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import * as roleService from "../../services/role.services";
import Breadcrums from "../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { globalLoader, handleServerValidations } from "../../../../utils/commonfunction";
import * as Yup from "yup";
import { Placeholder } from "rsuite";

const RoleEdit = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [permissionList, setPermissionList] = useState([]);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleTitle, setRoleTitle] = useState("");

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: "Sub Admins", url: "/admin/staff/list/1" },
    { title: "Roles", url: "/admin/role/list/1" },
    { title: t("btn_edit"), url: "" },
  ];

  useEffect(() => {
    Promise.all([
      roleService.PermissionsOptions(),
      roleService.Details(params.id),
    ]).then(([permsRes, roleRes]) => {
      setPermissionList(permsRes?.data || []);
      const role = roleRes?.data || {};
      setRoleTitle(role.title || "");
      const existingIds = (role.permissionIDs || role.permissions || []).map((p) =>
        typeof p === "object" ? String(p._id) : String(p)
      );
      setSelectedPermissionIds(existingIds);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [params.id]);

  const togglePermission = (id) => {
    setSelectedPermissionIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleGroup = (groupPermissions) => {
    const groupIds = groupPermissions.map((p) => p._id);
    const allSelected = groupIds.every((id) => selectedPermissionIds.includes(id));
    setSelectedPermissionIds((prev) =>
      allSelected ? prev.filter((id) => !groupIds.includes(id)) : [...new Set([...prev, ...groupIds])]
    );
  };

  const toggleAll = () => {
    const allIds = permissionList.map((p) => p._id);
    const allSelected = allIds.every((id) => selectedPermissionIds.includes(id));
    setSelectedPermissionIds(allSelected ? [] : allIds);
  };

  const groupedPermissions = permissionList.reduce((acc, p) => {
    const key = (p.groupName && String(p.groupName).trim()) ? String(p.groupName).trim() : "Other";
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});
  const groupKeys = Object.keys(groupedPermissions).sort();
  const allIds = permissionList.map((p) => p._id);
  const isAllSelected = allIds.length > 0 && allIds.every((id) => selectedPermissionIds.includes(id));

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required").trim(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { title: roleTitle },
    validateOnBlur: false,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      globalLoader(true);
      setSubmitted(true);
      roleService.Edit({ o_id: params.id, title: values.title.trim(), permissionIDs: selectedPermissionIds })
        .then((response) => {
          setSubmitting(false);
          globalLoader(false);
          if (response.success) {
            Swal.fire({ icon: "success", text: response.message, ...SWAL_SETTINGS });
            navigate("/admin/role/list/1");
          } else {
            Swal.fire({ icon: "error", text: handleServerValidations(response), ...SWAL_SETTINGS });
            setSubmitted(false);
          }
        })
        .catch(() => { setSubmitting(false); setSubmitted(false); globalLoader(false); });
    },
  });

  if (loading) {
    return (
      <>
        <Breadcrums data={breadcrumbs} />
        <div className="card custom-card"><div className="card-body"><Placeholder.Paragraph rows={8} active /></div></div>
      </>
    );
  }

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row row-sm">
          <div className="col-lg-12 col-md-12 animation_fade">
            <div className="card custom-card">
              <div className="card-body">
                <h6 className="main-content-label mb-3">Edit Role</h6>
                <div className="row row-sm">

                  <div className="col-md-12 form-group mb-4">
                    <label className="text-left d-flex fw-semibold">Title <span className="requirestar">*</span></label>
                    <input name="title" type="text" placeholder="Enter role title"
                      onChange={formik.handleChange} onBlur={formik.handleBlur}
                      value={formik.values.title} className="form-control" />
                    {formik.touched.title && formik.errors.title && (
                      <span className="text-danger d-flex">{formik.errors.title}</span>
                    )}
                  </div>

                    <div className="col-lg-12 form-group">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="main-content-label mb-0">Permissions <span className="requirestar">*</span></h6>
                      <div className="d-flex align-items-center p-2 border rounded bg-light cp" onClick={toggleAll}>
                        <input
                          type="checkbox"
                          className="cp m-0"
                          style={{ position: "relative", width: "18px", height: "18px", cursor: "pointer", flexShrink: 0, accentColor: "#6259ca" }}
                          checked={isAllSelected}
                          onChange={() => {}}
                        />
                        <span className="ms-2 fw-bold mb-0 cp fs-6 user-select-none">Check All Permissions</span>
                      </div>
                    </div>

                    {!permissionList.length ? (
                      <p className="text-muted">{t("msg_no_records")}</p>
                    ) : (
                      groupKeys.map((groupName) => {
                        const groupPermissions = groupedPermissions[groupName];
                        const groupIds = groupPermissions.map((p) => p._id);
                        const isGroupSelected = groupIds.every((id) => selectedPermissionIds.includes(id));
                        const selectedInGroup = groupPermissions.filter((p) => selectedPermissionIds.includes(p._id)).length;
                        return (
                          <div key={groupName} className="card shadow-none border mb-3 rounded-3">
                            <div className="card-header bg-light py-2 px-3 d-flex justify-content-between align-items-center">
                              <div className="d-flex align-items-center cp" onClick={() => toggleGroup(groupPermissions)}>
                                <input
                                  type="checkbox"
                                  className="cp m-0"
                                  style={{ position: "relative", width: "18px", height: "18px", cursor: "pointer", flexShrink: 0, accentColor: "#6259ca" }}
                                  checked={isGroupSelected}
                                  onChange={() => {}}
                                />
                                <span className="ms-2 text-primary mb-0 fw-bold cp fs-6 user-select-none">
                                  {groupName}
                                </span>
                              </div>
                              <span className="badge badge-light-primary text-primary fw-semibold">
                                {selectedInGroup} / {groupPermissions.length} selected
                              </span>
                            </div>
                            <div className="card-body p-3">
                              <div className="row g-2">
                                {groupPermissions.map((p) => {
                                  const isChecked = selectedPermissionIds.includes(p._id);
                                  return (
                                    <div key={p._id} className="col-xl-3 col-lg-4 col-md-6 col-sm-12">
                                      <div
                                        className={`p-2 border rounded-2 d-flex align-items-center ${
                                          isChecked ? "border-primary bg-primary-transparent" : "bg-white"
                                        }`}
                                        style={{ cursor: "pointer", transition: "all 0.15s ease" }}
                                        onClick={() => togglePermission(p._id)}
                                      >
                                        <input
                                          type="checkbox"
                                          className="cp m-0"
                                          style={{ position: "relative", width: "16px", height: "16px", cursor: "pointer", flexShrink: 0, accentColor: "#6259ca" }}
                                          checked={isChecked}
                                          onChange={() => {}}
                                        />
                                        <span
                                          className="ms-2 mb-0 cp text-truncate user-select-none"
                                          style={{ fontSize: "13px" }}
                                          title={p.title || p.shortSlug || p._id}
                                        >
                                          {p.title || p.shortSlug || p._id}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div className="mt-4">
                    <button className="btn btn-info mr-2" type="submit" disabled={submitted}>
                      {t("btn_save")}
                    </button>
                    <button className="btn ripple btn-secondary" type="button"
                      disabled={submitted} onClick={() => navigate("/admin/role/list/1")}>
                      {t("btn_cancel")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default RoleEdit;
